import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChange} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Role } from "../../../models/role";
import {Bot} from "../../../models/bot";
import { RoleControllerService } from "../../../services/role-controller/role-controller.service";
import { Store } from "../../../services/store.service";
import { BotControllerService } from "../../../services/role-controller/bot-controller.service";
import { MuleControllerService } from "../../../services/mule-controller/mule-controller.service";
import { MuleService } from "../../../models/mule-service";
import { TranslatedBaseError } from "../../../components/pages/TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { map } from 'rxjs/operators';

@Component({
  selector: "bot-list",
  templateUrl: "./bot-list.component.html",
  providers: [RoleControllerService],
})
export class BotListComponent extends TranslatedBaseError implements OnInit, OnChanges {

  @Output() public onBotAddEvent = new EventEmitter();
  @Output() public onBotEditEvent = new EventEmitter();
  @Input()  public bots: Bot[];
  public botsFilter: any = { name: "" };
  public muleServices: MuleService[] = [];
  public isReady: boolean = false;

  constructor(private roleService: RoleControllerService,
              private route: ActivatedRoute,
              private botController: BotControllerService,
              private muleService: MuleControllerService,
              public store: Store, toastr: ToastrService,
              public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params.serviceId) {
        this.store.state.common.serviceId = params.serviceId;
      }
    });
    const promises = [];
    this.bots?.filter((b) => {
      return b.botType === "internal";
    }).forEach((b) => {
      promises.push(
        this.botController.getBot(b.id).pipe(map((fullbot) => {
            return fullbot;
        })).toPromise(),
      );
    });
    Promise.all<any[]>(promises).then((results: any[]) => {
      this.loadMuleServices();
      this.bots?.filter((b) => {
        return b.botType === "internal";
      }).forEach((b) => {
        const newB = results.find((n) => n.id === b.id);
        if (newB) {
          b.inputFieldsMapping = newB.inputFieldsMapping;
          b.outputFieldsMapping = newB.outputFieldsMapping;
        }
      });
    });
  }

  public loadMuleServices(): void {
    this.muleService.getServicesList().subscribe(
      (data) => {
        this.isReady = true;
        this.muleServices = data;
        this.bots?.filter((b) => {
          return b.botType === "data";
        }).forEach((bot) => {
          const existingDB = this.muleServices.find((d) => {
            return d.id === bot.botServiceId;
          });
          if (!existingDB) {
            bot.botServiceId = null;
          } else {
            bot.latestBotServiceId = this.checkVersion(bot);
          }
        });
      },
      (err) => this.showErrorMessage(err),
    );
  }

  public checkVersion(bot) {
    if (!bot.botServiceId) {
      return false;
    }
    const services = this.muleServices.filter((s) => {
      return (this.getServiceNamePrefix(s.id) === this.getServiceNamePrefix(bot.botServiceId))
        && (this.getServiceNameSuffix(s.id) === this.getServiceNameSuffix(bot.botServiceId));
    });
    const newer = services.filter((s) => {
      return this.getServiceVersionNumber(s.id) > this.getServiceVersionNumber(bot.botServiceId);
    });
    return !newer.length;
  }

  public getServiceNamePrefix(name) {
    const n = name ? name.split("(")[0] : "";
    return String(n);
  }

  public getServiceNameSuffix(name) {
    const n = name ? name.split(")")[name.split(")").length - 1] : "";
    return String(n);
  }

  public getServiceVersionNumber(name) {
    const endName = name.slice(name.lastIndexOf("("));
    const regExp = /\(([^)]+)\)/;
    const matches = regExp.exec(endName);
    return matches ? Number(matches[1].replace(".", "")) : 0;
}

  public ngOnChanges(changes: { [propName: string]: SimpleChange }) {
    if (changes.bots) {
      if (changes.bots.currentValue) {
        this.bots = changes.bots.currentValue;
      }
    }
  }

  public dropped(event: CdkDragDrop<Role[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  public openAddModal() {
    window.scrollTo(0, 0);
    this.store.state.common.determinantSection.topLayer = "edit";
    this.onBotAddEvent.emit();
    }

  public openEditModal(bot) {
      this.onBotEditEvent.emit(bot);
  }

  public getInternalStatus(bot) {
    return bot.inputFieldsMapping?.length ? "bg-success text-light" : "bg-warning text-light";
  }

  public getInternalStatusTooltip(bot) {
    return bot.inputFieldsMapping?.length ? "Mapping started" : "Mapping is empty";
  }

  public getMessageStatus(bot) {
    return bot.selectedMessage && bot.selectedReceiver ? "bg-success text-light" : "bg-warning text-light";
  }

  public getMessageStatusTooltip(bot) {
    return bot.selectedMessage && bot.selectedReceiver ? "Message Bot is ready" : "Message Bot is not complete";
  }

  public getDataStatus(bot) {
    if (!bot.botServiceId) {
      return "bg-warning text-light";
    }
    return bot.latestBotServiceId ? "bg-success text-light" : "bg-danger text-light";
  }

  public getDataStatusTooltip(bot) {
    if (!bot.botServiceId) {
      return "No service selected";
    }
    return bot.latestBotServiceId ? "Latest service" : "Newer service available";
  }

}
